import { Download as DownloadIcon, FileBox, Tag, ExternalLink } from "lucide-react";
import { resolveMediaUrl } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { useLang } from "@/i18n/LangContext";

export default function Download({ modInfo }) {
    const { t, lang } = useLang();
    if (!modInfo) return null;

    const mainPkg = {
        name: "Project Ruda Śląska",
        kind: "Mapa główna",
        version: modInfo.version,
        size: modInfo.file_size,
        requirements: modInfo.requirements,
        changelog: modInfo.changelog,
        url: modInfo.download_url,
        type: modInfo.download_type,
        icon: DownloadIcon,
        variant: "yellow",
    };

    return (
        <section
            id="pobierz"
            data-testid="download-section"
            className="relative py-24 md:py-32"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <div className="grid lg:grid-cols-12 gap-10 items-end mb-12">
                    <div className="lg:col-span-5">
                        <div className="label-eyebrow mb-4">{t("04 - Pobierz")}</div>
                        <h2 className="font-display uppercase text-4xl md:text-5xl lg:text-6xl tracking-tighter text-white mb-6">
                            {lang === "en" ? (
                                t("Wsiadaj w trasę")
                            ) : (
                                <>Wsiadaj<br />w trasę</>
                            )}
                        </h2>
                        <p className="text-base md:text-lg text-[color:var(--text-secondary)] leading-relaxed">
                            {t("Pobierz aktualną wersję głównej mapy Project Ruda Śląska. Dodatki rozszerzające mapę znajdziesz w sekcji poniżej.")}
                        </p>
                    </div>
                </div>

                <div className="grid gap-6 lg:grid-cols-1">
                    <PackageCard pkg={mainPkg} primary testid="package-main" />
                </div>
            </div>
        </section>
    );
}

function PackageCard({ pkg, primary = false, testid }) {
    const { t } = useLang();
    const downloadHref = pkg.url ? resolveMediaUrl(pkg.url) : null;
    const isExternal = pkg.type === "external";
    const Icon = pkg.icon || DownloadIcon;
    const isGreen = pkg.variant === "green";

    // Color tokens per variant
    const accent = isGreen ? "#34d399" : "#facc15";
    const accentSoft = isGreen ? "rgba(52,211,153,0.10)" : "rgba(250,204,21,0.10)";
    const borderColor = isGreen ? "rgba(52,211,153,0.30)" : "rgba(250,204,21,0.18)";
    const borderStrong = isGreen ? "rgba(52,211,153,0.55)" : "rgba(250,204,21,0.55)";
    const labelStyle = { color: accent };

    return (
        <div
            data-testid={testid}
            className="p-8 md:p-10 relative overflow-hidden flex flex-col rounded-xl transition-all duration-300"
            style={{
                background: "var(--bg-surface)",
                border: `1px solid ${primary ? borderStrong : borderColor}`,
            }}
        >
            <div
                className="absolute -top-20 -right-20 w-72 h-72 rounded-full blur-3xl pointer-events-none"
                style={{ background: accentSoft }}
            />

            <div className="flex items-start justify-between mb-6 relative gap-4">
                <div>
                    <div
                        className="text-[10px] uppercase tracking-[0.3em] mb-1.5"
                        style={labelStyle}
                    >
                        {t(pkg.kind)}
                    </div>
                    <h3 className="font-display uppercase text-2xl md:text-3xl text-white tracking-tight">
                        {pkg.name}
                    </h3>
                </div>
                <Icon size={28} strokeWidth={1.4} style={{ color: accent }} className="shrink-0" />
            </div>

            <div className="grid grid-cols-2 gap-5 mb-7 relative">
                <Stat icon={Tag} label="Wersja" value={pkg.version || "-"} color={accent} />
                <Stat icon={FileBox} label="Rozmiar" value={pkg.size || "-"} color={accent} />
            </div>

            <div className="relative mt-auto">
                {downloadHref ? (
                    <a
                        data-testid={`${testid}-btn`}
                        href={downloadHref}
                        target={isExternal ? "_blank" : undefined}
                        rel={isExternal ? "noopener noreferrer" : undefined}
                        download={!isExternal}
                        onClick={() => trackEvent("download_click", { label: pkg.name || "main" })}
                        className="inline-flex items-center justify-center gap-2 px-7 py-4 font-semibold uppercase tracking-wider text-sm transition-all duration-300 w-full rounded-[4px]"
                        style={{
                            background: accent,
                            color: "var(--bg-base)",
                            border: `1px solid ${accent}`,
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = "translateY(-2px)";
                            e.currentTarget.style.boxShadow = `0 8px 30px ${accent}55`;
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "translateY(0)";
                            e.currentTarget.style.boxShadow = "none";
                        }}
                    >
                        <DownloadIcon size={18} /> {t("Pobierz")}
                        {isExternal && <ExternalLink size={14} />}
                    </a>
                ) : (
                    <div
                        data-testid={`${testid}-unavailable`}
                        className="text-center py-4 text-[color:var(--text-secondary)] border border-dashed border-white/10 rounded-md text-sm"
                    >
                        Plik do pobrania nie jest jeszcze dostępny.
                    </div>
                )}
            </div>

            {pkg.requirements && (
                <div className="mt-7 relative">
                    <div
                        className="text-xs font-semibold uppercase tracking-[0.25em] mb-2"
                        style={labelStyle}
                    >
                        {t("Wymagania")}
                    </div>
                    <p className="text-sm text-[color:var(--text-secondary)] leading-relaxed">
                        {t(pkg.requirements)}
                    </p>
                </div>
            )}

            {pkg.changelog && (
                <div className="mt-6 relative">
                    <div
                        className="text-xs font-semibold uppercase tracking-[0.25em] mb-2"
                        style={labelStyle}
                    >
                        {primary ? t("Changelog") : t("Opis")}
                    </div>
                    <pre className="text-sm text-[color:var(--text-secondary)] leading-relaxed whitespace-pre-wrap font-sans">
                        {t(pkg.changelog)}
                    </pre>
                </div>
            )}
        </div>
    );
}

function Stat({ icon: Icon, label, value, color }) {
    const { t } = useLang();
    return (
        <div>
            <Icon size={18} className="mb-2" strokeWidth={1.5} style={{ color }} />
            <div className="text-[10px] uppercase tracking-[0.25em] text-[color:var(--text-muted)]">
                {t(label)}
            </div>
            <div className="font-display text-xl text-white">{value}</div>
        </div>
    );
}
